import { useNavigate } from "@opendash/router";
import { Button, Modal, Pagination, Select, Switch } from "antd";
import React from "react";
import styled from "styled-components";

import {
  MarkerCanvas,
  produce,
  useElementSize,
  useTranslation,
} from "@opendash/core";
import { $parse } from "@opendash/plugin-parse";
import { createComponent } from "@opendash/state";
import { DocumentPage } from "../types-generated";
import type { AppState } from "./App.state";
import { ActionBar } from "./styles";

const Container = styled.div`
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`;

const PageImageContainer = styled.div`
  width: 100%;
`;

const PageImage = styled.div`
  width: 100%;
  box-shadow:
    rgba(0, 0, 0, 0.12) 0px 1px 3px,
    rgba(0, 0, 0, 0.24) 0px 1px 2px;
`;

const PageContent = styled.div`
  width: 600px;
`;

const Marker = styled.div`
  /* width: 30px; */
  width: auto;
  padding: 0 10px;
  height: 30px;
  line-height: 30px;
  font-size: 16px;

  color: white;
  background: var(--ant-primary-color);

  display: inline-block;
  text-align: center;
  border-radius: 20px;
  border-top-left-radius: 0px;
  outline: 0;

  border: 1px solid rgba(0, 0, 0, 0.2);
`;

interface AnnotationInterface {
  key: string;
  x: number;
  y: number;
  articleId: string;
}

export const KnowledgeDocumentView = createComponent(
  function KnowledgeDocumentView({ state }: { state: AppState }) {
    const t = useTranslation();
    const navigate = useNavigate();

    if (!state.document) {
      return null;
    }

    if (state.documentPages.length === 0) {
      return null;
    }

    const page = state.documentPages[state.documentPageIndex];

    if (!page) {
      return null;
    }

    return (
      <Container>
        <ImageComponent
          key={state.documentPageIndex}
          state={state}
          page={page}
        />
      </Container>
    );
  }
);

export const ImageComponent = createComponent<{
  state: AppState;
  page: DocumentPage;
}>(function ImageComponent({ state, page }) {
  const t = useTranslation();
  const navigate = useNavigate();
  const imageContainerRef = React.useRef<HTMLDivElement>();
  const { width } = useElementSize(imageContainerRef);

  const [updateMarker, setUpdateMarker] =
    React.useState<AnnotationInterface | null>(null);

  const [edit, setEdit] = React.useState<boolean>(false);

  const height = width * (2828 / 2000);

  const imageUrl = page.image.url();

  const pageAnnotations: AnnotationInterface[] = page.annotations;
  const documentAnnotations: AnnotationInterface[] = page.annotations;

  state.useForceRender();

  return (
    <>
      {$parse.ui.getObjectPermission(state.document, "update") && (
        <ActionBar>
          {$parse.ui.getObjectPermission(state.document, "update") && (
            <Button
              children={t("knowledge:label.update_document")}
              onClick={() => {
                state.updateContent(state.document);
              }}
            />
          )}
          {$parse.ui.getObjectPermission(state.document, "delete") && (
            <Button
              danger
              children={t("knowledge:label.delete_document")}
              onClick={() => {
                state.deleteContent(state.document).then((deleted) => {
                  if (deleted) {
                    navigate("../../..");
                  }
                });
              }}
            />
          )}
          {$parse.ui.getObjectPermission(page, "update") && (
            <span>
              {t("knowledge:label.edit_document_page_annotations")}{" "}
              <Switch
                checked={edit}
                onChange={(isChecked) => {
                  setEdit(isChecked);
                }}
              />
            </span>
          )}
        </ActionBar>
      )}
      <PageContainer>
        <PageImageContainer>
          <PageImage ref={imageContainerRef} style={{ height }}>
            <MarkerCanvas
              edit={edit}
              width={width}
              height={height}
              imageWidth={2000}
              imageHeight={2828}
              imageUrl={imageUrl}
              onCreateMarker={(key, x, y) => {
                setUpdateMarker({
                  key,
                  x,
                  y,
                  articleId: null,
                });
              }}
              onUpdateMarkerPosition={async (key, x, y) => {
                await page.save({
                  annotations: produce(pageAnnotations, (draft) => {
                    const annotation = draft.find((a) => a.key === key);

                    annotation.x = x;
                    annotation.y = y;
                  }),
                });
              }}
              onMarkerDoubleClick={(key) => {
                setUpdateMarker(pageAnnotations.find((a) => a.key === key));
              }}
              markers={Object.fromEntries(
                pageAnnotations.map((annotation) => [
                  annotation.key,
                  {
                    x: annotation.x,
                    y: annotation.y,
                    render: (props) => {
                      const article = state.articles.find(
                        (a) => a.id === annotation.articleId
                      );

                      if (!article) {
                        return null;
                      }

                      return <Marker {...props}>{article.label}</Marker>;
                    },
                  },
                ])
              )}
            />
          </PageImage>
        </PageImageContainer>
        <PageContent>
          <Pagination
            current={page.page}
            pageSize={1}
            total={state.documentPages.length}
            onChange={(p) => {
              navigate(
                `/knowledgebase/${state.language}/${state.category.id}/document/${state.document.id}/${p}`
              );
            }}
          />
        </PageContent>
        <Modal
          open={!!updateMarker}
          onOk={async () => {
            await page.save({
              annotations: [
                ...pageAnnotations.filter((a) => a.key !== updateMarker.key),
                updateMarker,
              ],
            });

            setUpdateMarker(null);
          }}
          onCancel={() => {
            setUpdateMarker(null);
          }}
        >
          {updateMarker && (
            <Select
              value={updateMarker.articleId}
              onChange={(articleId) => {
                setUpdateMarker((current) => ({ ...current, articleId }));
              }}
              options={state.articles.map((article) => ({
                value: article.id,
                label: article.label,
              }))}
              style={{ width: "100%" }}
            />
          )}
        </Modal>
      </PageContainer>
    </>
  );
});
